import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ClipboardCheck, FileUp, Loader2, Printer, Save, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { orderReviewsAPI } from '../../services/api';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { ChecklistTable, ChoiceRow, Field, SECTION_TITLE } from '../../components/order-review/fields';
import { PartReviewSection } from '../../components/order-review/PartReviewSection';

const STATUS_BADGE = {
  pending: ['Review pending', 'bg-amber-100 text-amber-800'],
  draft: ['In progress — not generated', 'bg-amber-100 text-amber-800'],
  downloaded: ['Generated — awaiting signed copy', 'bg-blue-100 text-blue-800'],
  signed_uploaded: ['Signed copy uploaded', 'bg-emerald-100 text-emerald-800'],
};

const Section = ({ title, subtitle, children, testId }) => (
  <section className="space-y-4 border bg-white p-5" data-testid={testId}>
    <div>
      <h2 className={SECTION_TITLE}>{title}</h2>
      {subtitle && <p className="text-xs text-slate-500">{subtitle}</p>}
    </div>
    {children}
  </section>
);

export default function OrderReviewSheetPage() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [sheet, setSheet] = useState(null);
  const [exists, setExists] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const fileInput = useRef(null);

  const load = useCallback(async () => {
    try {
      const response = await orderReviewsAPI.getForOrder(orderId);
      setExists(Boolean(response.data.exists));
      setSheet(response.data.exists ? response.data : null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not load the review sheet');
    } finally { setLoading(false); }
  }, [orderId]);

  useEffect(() => { load(); }, [load]);

  const createSheet = async () => {
    setBusy(true);
    try {
      const response = await orderReviewsAPI.create(orderId);
      setSheet(response.data); setExists(true);
      toast.success(`Review sheet ${response.data.review_sheet_no} created with order data pre-filled`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not create the review sheet');
    } finally { setBusy(false); }
  };

  const patch = (changes) => { setSheet((current) => ({ ...current, ...changes })); setDirty(true); };

  const save = async () => {
    setBusy(true);
    try {
      const response = await orderReviewsAPI.save(sheet.id, {
        order_details: sheet.order_details, order_review: sheet.order_review, parts: sheet.parts,
        order_schedule: sheet.order_schedule, outcome: sheet.outcome, approvals: sheet.approvals,
      });
      setSheet((current) => ({ ...current, updated_at: response.data.updated_at, contributors: response.data.contributors }));
      setDirty(false);
      toast.success('Review sheet saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  const openPrint = async () => {
    if (dirty) await save();
    window.open(`/order-reviews/${sheet.id}/print`, '_blank');
    setTimeout(load, 2500);
  };

  const uploadSigned = async (file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    setBusy(true);
    try {
      await orderReviewsAPI.uploadSigned(sheet.id, formData);
      toast.success('Signed sheet uploaded');
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally { setBusy(false); if (fileInput.current) fileInput.current.value = ''; }
  };

  const removeSigned = async (fileId) => {
    try { await orderReviewsAPI.deleteSigned(sheet.id, fileId); await load(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Could not remove the file'); }
  };

  const downloadSigned = async (file) => {
    try {
      const response = await orderReviewsAPI.downloadSigned(sheet.id, file.id);
      const url = URL.createObjectURL(response.data);
      const anchor = document.createElement('a');
      anchor.href = url; anchor.download = file.filename; anchor.click();
      URL.revokeObjectURL(url);
    } catch (error) { toast.error('Download failed'); }
  };

  if (loading) {
    return <div className="flex min-h-[400px] items-center justify-center" data-testid="order-review-loading"><Loader2 className="h-8 w-8 animate-spin" /></div>;
  }

  if (!exists) {
    return (
      <div className="mx-auto max-w-2xl space-y-5 py-16 text-center" data-testid="order-review-empty">
        <ClipboardCheck className="mx-auto h-12 w-12 text-blue-600" />
        <h1 className="font-heading text-2xl font-bold">Order Review &amp; Feasibility Sheet</h1>
        <p className="text-sm text-slate-500">
          Contract review as per IATF 16949 cl. 8.2.3 (F-MKT-04). Generating the sheet pre-fills the order, customer,
          PO and quotation reference, every part line, material/grade, drawing revision, process routing from the
          part&apos;s process flow and the raw material from its costing — then your team edits only what is missing.
        </p>
        <div className="flex justify-center gap-2">
          <Button variant="outline" onClick={() => navigate(`/orders/${orderId}`)} data-testid="order-review-back-empty">Back to order</Button>
          <Button onClick={createSheet} disabled={busy} data-testid="order-review-create-btn">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ClipboardCheck className="mr-2 h-4 w-4" />}
            Generate review sheet
          </Button>
        </div>
      </div>
    );
  }

  const [statusLabel, statusClass] = STATUS_BADGE[sheet.status] || STATUS_BADGE.draft;
  const details = sheet.order_details || {};
  const setDetail = (key, value) => patch({ order_details: { ...details, [key]: value } });

  return (
    <div className="space-y-5 animate-fade-in" data-testid="order-review-page">
      <header className="flex flex-col gap-3 border-b pb-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <Button variant="ghost" className="mb-2 px-0" onClick={() => navigate(`/orders/${orderId}`)} data-testid="order-review-back">
            <ArrowLeft className="mr-2 h-4 w-4" /> {sheet.order_number}
          </Button>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="font-mono text-2xl font-bold sm:text-3xl" data-testid="order-review-number">{sheet.review_sheet_no}</h1>
            <Badge className={statusClass} data-testid="order-review-status">{statusLabel}</Badge>
          </div>
          <p className="mt-1 text-xs text-slate-500">
            {sheet.doc_no} Rev {sheet.doc_rev} · Order Review &amp; Feasibility Sheet · IATF 16949 cl. 8.2.3
            {sheet.updated_by_name && ` · last saved by ${sheet.updated_by_name}`}
            {sheet.contributors?.length > 1 && ` · contributors: ${sheet.contributors.join(', ')}`}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => fileInput.current?.click()} disabled={busy} data-testid="order-review-upload-btn">
            <FileUp className="mr-2 h-4 w-4" /> Upload signed sheet
          </Button>
          <input ref={fileInput} type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.webp,.tif,.tiff"
            onChange={(event) => uploadSigned(event.target.files?.[0])} data-testid="order-review-file-input" />
          <Button variant="outline" onClick={openPrint} data-testid="order-review-print-btn">
            <Printer className="mr-2 h-4 w-4" /> Print / download
          </Button>
          <Button onClick={save} disabled={busy || !dirty} data-testid="order-review-save-btn">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            {dirty ? 'Save changes' : 'Saved'}
          </Button>
        </div>
      </header>

      {(sheet.signed_files || []).length > 0 && (
        <div className="border border-emerald-200 bg-emerald-50 p-4" data-testid="order-review-signed-files">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-emerald-800">Signed copies</p>
          {sheet.signed_files.map((file) => (
            <div key={file.id} className="flex items-center gap-3 text-sm" data-testid={`signed-file-${file.id}`}>
              <button type="button" className="font-medium text-emerald-900 underline" onClick={() => downloadSigned(file)}>{file.filename}</button>
              <span className="text-xs text-emerald-700">
                {new Date(file.uploaded_at).toLocaleString('en-IN')} · {file.uploaded_by_name}
              </span>
              <Button size="icon" variant="ghost" onClick={() => removeSigned(file.id)} data-testid={`signed-file-delete-${file.id}`}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}

      <Section title="1 · Order details" testId="order-review-section-details">
        <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-4">
          <Field label="Customer" value={details.customer} onChange={(v) => setDetail('customer', v)} testId="review-customer" />
          <Field label="Customer PO no." value={details.customer_po_no} onChange={(v) => setDetail('customer_po_no', v)} testId="review-po-no" />
          <Field label="PO date" type="date" value={(details.po_date || '').slice(0, 10)} onChange={(v) => setDetail('po_date', v)} testId="review-po-date" />
          <Field label="Order acceptance no." value={details.order_acceptance_no} onChange={(v) => setDetail('order_acceptance_no', v)} testId="review-oa-no" />
          <Field label="OA date" type="date" value={(details.oa_date || '').slice(0, 10)} onChange={(v) => setDetail('oa_date', v)} testId="review-oa-date" />
          <Field label="Customer delivery date" type="date" value={(details.customer_delivery_date || '').slice(0, 10)} onChange={(v) => setDetail('customer_delivery_date', v)} testId="review-delivery-date" />
          <Field label="Order value" value={details.order_value} onChange={(v) => setDetail('order_value', v)} testId="review-order-value" />
          <Field label="Currency" value={details.currency} onChange={(v) => setDetail('currency', v)} testId="review-currency" />
          <Field label="Quotation reference" value={details.quotation_reference} onChange={(v) => setDetail('quotation_reference', v)} testId="review-quotation-ref" />
          <Field label="Incoterm / destination" value={details.incoterm_destination} onChange={(v) => setDetail('incoterm_destination', v)} testId="review-incoterm" />
          <Field label="Drawing / spec. rev." value={details.drawing_spec_rev} onChange={(v) => setDetail('drawing_spec_rev', v)} testId="review-drawing-rev" />
          <Field label="CSR reference" value={details.csr_ref} onChange={(v) => setDetail('csr_ref', v)} testId="review-csr" />
          <Field label="PPAP / sample level" value={details.ppap_level} onChange={(v) => setDetail('ppap_level', v)} placeholder="e.g. Level 3" testId="review-ppap-level" />
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <ChoiceRow label="Order type" options={[{ value: 'new', label: 'New' }, { value: 'repeat', label: 'Repeat' }, { value: 'modified', label: 'Modified (eng. change)' }]}
            value={details.order_type} onChange={(v) => setDetail('order_type', v)} testId="review-order-type" />
          <ChoiceRow label="PPAP / sample required" options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]}
            value={details.ppap_required} onChange={(v) => setDetail('ppap_required', v)} testId="review-ppap" />
        </div>
      </Section>

      <Section title="2 · Parts on this order" subtitle="One part review sheet per line — open each tab below" testId="order-review-section-parts-summary">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-sm">
            <thead>
              <tr className="border-b text-left text-[11px] uppercase tracking-wide text-slate-500">
                <th className="w-8 py-2">#</th><th className="py-2">Part no. / description</th><th className="py-2">Size</th>
                <th className="py-2">Material / grade</th><th className="py-2 text-right">Order qty</th>
                <th className="py-2">Delivery date</th><th className="py-2">Part sheet no.</th><th className="py-2">Feasible?</th>
              </tr>
            </thead>
            <tbody>
              {(sheet.parts || []).map((part, index) => (
                <tr key={part.id} className="border-b last:border-0" data-testid={`review-part-summary-${index + 1}`}>
                  <td className="py-2 text-slate-400">{index + 1}</td>
                  <td className="py-2">
                    <span className="font-mono text-xs">{part.identification?.sil_part_code || '—'}</span>
                    <span className="block text-xs text-slate-500">{part.identification?.description}</span>
                  </td>
                  <td className="py-2">{part.identification?.size_thread || '—'}</td>
                  <td className="py-2">{part.identification?.material_grade || '—'}</td>
                  <td className="py-2 text-right font-mono">{part.identification?.order_qty ?? '—'}</td>
                  <td className="py-2">{(part.identification?.delivery_date || '—').slice(0, 10)}</td>
                  <td className="py-2 font-mono text-xs">{part.part_sheet_no}</td>
                  <td className="py-2">{part.feasible ? part.feasible.replace('_', ' ') : <span className="text-amber-600">pending</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <Section title="3 · Order-level review" subtitle="Marketing / Sales" testId="order-review-section-order-review">
        <ChecklistTable items={sheet.order_review || []} testIdPrefix="order-review-check"
          onChange={(index, changes) => patch({
            order_review: sheet.order_review.map((item, idx) => (idx === index ? { ...item, ...changes } : item)),
          })} />
      </Section>

      <Section title="Part review sheets" subtitle="Complete one per line item" testId="order-review-section-part-sheets">
        {(sheet.parts || []).length === 0 ? (
          <p className="text-sm text-slate-500">This order has no line items.</p>
        ) : (
          <Tabs defaultValue="part-0">
            <TabsList className="mb-4 h-auto w-full justify-start overflow-x-auto bg-slate-100 p-1">
              {sheet.parts.map((part, index) => (
                <TabsTrigger key={part.id} value={`part-${index}`} data-testid={`review-part-tab-${index + 1}`}>
                  {index + 1}. {part.identification?.sil_part_code || part.identification?.description?.slice(0, 18) || `Part ${index + 1}`}
                </TabsTrigger>
              ))}
            </TabsList>
            {sheet.parts.map((part, index) => (
              <TabsContent key={part.id} value={`part-${index}`}>
                <PartReviewSection part={part} index={index}
                  onChange={(updated) => patch({ parts: sheet.parts.map((item, idx) => (idx === index ? updated : item)) })} />
              </TabsContent>
            ))}
          </Tabs>
        )}
      </Section>

      <Section title="4 · Order schedule" subtitle="Consolidated from all part review sheets" testId="order-review-section-schedule">
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="Earliest dispatch (first part)" type="date" value={sheet.order_schedule?.earliest_dispatch}
            onChange={(v) => patch({ order_schedule: { ...sheet.order_schedule, earliest_dispatch: v } })} testId="review-earliest-dispatch" />
          <Field label="Committed delivery date(s) to customer" value={sheet.order_schedule?.committed_delivery}
            onChange={(v) => patch({ order_schedule: { ...sheet.order_schedule, committed_delivery: v } })} testId="review-committed-delivery" />
          <Field label="Final dispatch (last part)" type="date" value={sheet.order_schedule?.final_dispatch}
            onChange={(v) => patch({ order_schedule: { ...sheet.order_schedule, final_dispatch: v } })} testId="review-final-dispatch" />
        </div>
      </Section>

      <Section title="5 · Review outcome" testId="order-review-section-outcome">
        <ChoiceRow label="Outcome" options={[
          { value: 'accepted', label: 'Order accepted' },
          { value: 'accepted_with_conditions', label: 'Accepted with conditions' },
          { value: 'not_feasible', label: 'Not feasible — customer to be informed' },
        ]} value={sheet.outcome?.result} onChange={(v) => patch({ outcome: { ...sheet.outcome, result: v } })} testId="review-outcome" />
        <Field label="Conditions / deviations communicated to customer / remarks" value={sheet.outcome?.remarks}
          onChange={(v) => patch({ outcome: { ...sheet.outcome, remarks: v } })} testId="review-outcome-remarks" />
      </Section>

      <Section title="6 · Review & approval" subtitle="Names print on the sheet; signatures are signed by hand and the signed copy uploaded back here" testId="order-review-section-approvals">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[700px] text-sm">
            <thead>
              <tr className="border-b text-left text-[11px] uppercase tracking-wide text-slate-500">
                <th className="w-[20%] py-2">Function</th><th className="w-[34%] py-2">Responsibility</th>
                <th className="py-2">Name</th><th className="w-[18%] py-2">Date</th>
              </tr>
            </thead>
            <tbody>
              {(sheet.approvals || []).map((row, index) => (
                <tr key={row.id} className="border-b last:border-0" data-testid={`review-approval-row-${index + 1}`}>
                  <td className="py-2 font-medium">{row.function}</td>
                  <td className="py-2 text-slate-500">{row.responsibility}</td>
                  <td className="py-2 pr-2">
                    <Field label="" value={row.name} testId={`review-approval-${index + 1}-name`}
                      onChange={(v) => patch({ approvals: sheet.approvals.map((item, idx) => (idx === index ? { ...item, name: v } : item)) })} />
                  </td>
                  <td className="py-2">
                    <Field label="" type="date" value={row.date} testId={`review-approval-${index + 1}-date`}
                      onChange={(v) => patch({ approvals: sheet.approvals.map((item, idx) => (idx === index ? { ...item, date: v } : item)) })} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>
    </div>
  );
}
